import React, { JSX, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import OpenInFullIcon from "@mui/icons-material/OpenInFull";
import CloseFullscreenIcon from "@mui/icons-material/CloseFullscreen";
import { Button } from "components/common/Button";
import { useDialogBody } from "components/main/utils/dialogBody";
import { useEscapeStack } from "components/hooks/useEscapeStack";
import { prefersReducedMotion } from "@/lib/motion";

interface Box {
  top: number;
  left: number;
  width: number;
  height: number;
}

type Expansion =
  { phase: "opening" | "closing"; from: Box } | { phase: "open" };

interface ExpandInDialogProps {
  /** What grows, for the buttons: "Expand SQL" / "Collapse SQL". */
  label: string;
  children: (expanded: boolean) => React.ReactNode;
}

const MOVE = "var(--duration-short) var(--ease-move)";
const TRANSITION = ["top", "left", "width", "height", "padding"]
  .map((property) => `${property} ${MOVE}`)
  .join(", ");

/**
 * A block that grows from where it sits to fill the enclosing dialog's body
 * and shrinks back, from a hover button in its top-right corner. Renders the
 * block as it is outside a `Dialog`, with no button.
 */
export function ExpandInDialog({
  label,
  children,
}: ExpandInDialogProps): JSX.Element {
  const body = useDialogBody();
  const inlineRef = useRef<HTMLDivElement>(null);
  const overlayRef = useRef<HTMLDivElement>(null);
  const expandRef = useRef<HTMLButtonElement>(null);
  const refocus = useRef(false);
  const [expansion, setExpansion] = useState<Expansion | null>(null);

  const inlineBox = (): Box | null => {
    if (!inlineRef.current || !body || prefersReducedMotion()) return null;
    const block = inlineRef.current.getBoundingClientRect();
    const frame = body.getBoundingClientRect();
    return {
      top: block.top - frame.top,
      left: block.left - frame.left,
      width: block.width,
      height: block.height,
    };
  };

  const expand = () => {
    const from = inlineBox();
    setExpansion(from ? { phase: "opening", from } : { phase: "open" });
  };
  const collapse = () => {
    refocus.current = !!overlayRef.current?.contains(document.activeElement);
    const from = inlineBox();
    setExpansion(from ? { phase: "closing", from } : null);
  };

  useLayoutEffect(() => {
    if (expansion?.phase === "opening") {
      // Lay out the start box first, or the change to the full one jumps.
      overlayRef.current?.getBoundingClientRect();
      setExpansion({ phase: "open" });
    } else if (expansion === null && refocus.current) {
      refocus.current = false;
      expandRef.current?.focus();
    }
  }, [expansion]);

  useEscapeStack(expansion?.phase === "open", collapse);

  if (!body) return <>{children(false)}</>;

  const corner = (expanded: boolean) => {
    const name = `${expanded ? "Collapse" : "Expand"} ${label}`;
    return (
      <div
        className="absolute opacity-0 transition-opacity group-hover:opacity-100 has-[:focus-visible]:opacity-100"
        style={{
          top: "var(--spacing-sm)",
          right: "var(--spacing-sm)",
          transitionDuration: "var(--duration-micro)",
        }}
      >
        <Button
          ref={expanded ? undefined : expandRef}
          aria-label={name}
          variant="default"
          size="xs"
          autoFocus={expanded}
          onClick={expanded ? collapse : expand}
          className="bg-bg/60! hover:bg-bg/90! backdrop-blur-sm"
        >
          {expanded ? <CloseFullscreenIcon /> : <OpenInFullIcon />}
        </Button>
      </div>
    );
  };

  const open = expansion?.phase === "open";
  return (
    <>
      <div ref={inlineRef} className="group relative" inert={!!expansion}>
        {children(false)}
        {corner(false)}
      </div>
      {expansion &&
        createPortal(
          <div
            ref={overlayRef}
            data-testid="expanded-in-dialog"
            className="absolute z-10 bg-bg-subtle"
            style={{
              ...(open
                ? { top: 0, left: 0, width: "100%", height: "100%" }
                : expansion.from),
              padding: open ? "var(--spacing-lg)" : 0,
              transition: TRANSITION,
            }}
            onTransitionEnd={(e) => {
              if (e.target === e.currentTarget && expansion.phase === "closing")
                setExpansion(null);
            }}
          >
            <div className="group relative h-full">
              {children(true)}
              {corner(true)}
            </div>
          </div>,
          body,
        )}
    </>
  );
}
